import { Suspense, type ReactNode } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router';
import { Spinner } from '@monksflow/monks-ui';
import {
  AgentDetail,
  AnalysisHome,
  Analytics,
  AudienceDetail,
  Audiences,
  ContentStudioPage,
  ContentTestDetail,
  ContentTestOverview,
  ContentTestSetup,
  CreatePage,
  ContentBoardsPage,
  DeliverPage,
  FocusGroupDetail,
  FocusGroups,
  FolderGuidelines,
  HomePageDashboard,
  KnowledgeBase,
  KnowledgeBaseDetail,
  KnowledgeHub,
  MediaConnectors,
  NewAnalysis,
  NewFocusGroup,
  NewProject,
  NotFound,
  OpportunitiesRoom,
  Download,
  PlanPage,
  TrackerBoard,
  Trackers,
  ProjectDetail,
  Projects,
  Root,
  ScalePage,
  Sessions,
  StreamChatSession,
  ToolsCategoryPage,
  ToolsHub,
  VisualGuidelines,
  NACBIntakeFormPage,
} from '@/pages';
import { MediaGuidelines } from '@/pages/MediaGuidelines/MediaGuidelines';
import { OrganizationSettingsLayout } from '@/pages/Admin/OrganizationSettingsLayout';
import { EtlConfigPage } from '@/pages/Admin/EtlConfig';
import { AdFormatsPage } from '@/pages/Admin/AdFormats';
import { CustomizationPage } from '@/pages/Admin/Customization';
import { ModelProvidersPage } from '@/pages/Admin/Models';
import { McpCatalogPage } from '@/pages/Admin/McpCatalog';
import { MyMcpsPage } from '@/pages/MyMcps';
import { ConnectedAppsPage } from '@/pages/Admin/ConnectedApps';
import {
  AgentConfigPage,
  AudienceAgentConfigPage,
  ExternalToolsAgentConfigPage,
  ToolConfigPage,
  ToolsPage,
  AgentBuilderPage,
} from '@/pages/Admin/Tools';
import { AnalysisResult } from '@/pages/AnalysisResult';
import { RequireWidgets, WidgetRedirect } from '@/pages/Admin/Widgets';
import {
  AudienceProfiles,
  BrandXray,
  BusinessChallenge,
  BusinessOpportunities,
  CategoryCompetition,
  CultureContext,
  ExecutiveSnapshot,
  IQPersonaDetail,
  MarketingPerformance,
  CreativeLandscape,
  MediaLandscape,
} from '@/pages/AnalysisResult/sections';
import { BrandMediaLibraryPage } from '@/pages/BrandMediaLibrary';
import { BriefTemplateBuilder } from '@/pages/KnowledgeHub/BriefTemplateBuilder';
import { BriefTemplateComingSoon } from '@/pages/KnowledgeHub/BriefTemplateComingSoon';
import { BriefTemplatesList } from '@/pages/KnowledgeHub/BriefTemplatesList';
import { TaxonomyLayout } from '@/pages/KnowledgeHub/TaxonomyLayout';
import { TaxonomyList } from '@/pages/KnowledgeHub/TaxonomyList';
import { TaxonomyDetail } from '@/pages/KnowledgeHub/TaxonomyDetail';
import { TaxonomyLists } from '@/pages/KnowledgeHub/TaxonomyLists';
import { TaxonomyFields } from '@/pages/KnowledgeHub/TaxonomyFields';
import { TaxonomyCategories } from '@/pages/KnowledgeHub/TaxonomyCategories';
import { TaxonomyGenerationList } from '@/pages/TaxonomyGeneration/TaxonomyGenerationList';
import { TaxonomyGenerationDetail } from '@/pages/TaxonomyGeneration/TaxonomyGenerationDetail';
import { ProjectBriefDetail } from '@/pages/ProjectBriefDetail';
import {
  AssetCompareView,
  AssetDetail,
  Audience,
  Briefing,
  Creative,
  Delivery,
  FolderDetailPage,
  FoldersListPage,
  JobReview,
  Library,
  ManageCampaigns,
  Overview,
  Pacing,
  Performance,
  PerformanceOverview,
} from '@/pages/ProjectDetail/sections';
import { EmailTemplatesList } from '@/pages/KnowledgeHub/EmailTemplatesList';
import { EmailTemplateEditor } from '@/pages/KnowledgeHub/EmailTemplateEditor';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { Loading } from '@/shared/ui/Loading';
import { FeatureFlagGuard, ManagedToolGuard } from '@/shared/ui/guards';
import { ROUTE_PATTERNS, ROUTES } from '@/shared/config/routes';
import { DashboardLayout, ProtectedRoute } from '@/widgets/app-shell';
import { ApprovalsGuardedOutlet } from './ApprovalsGuardedOutlet';
import { ContentStudioRouteGuard } from './ContentStudioRouteGuard';
import { PaddedOutlet } from './PaddedOutlet';
import { NotFoundRoute } from './NotFoundRoute';
import { AdminIndexRedirect, LegacyKBRedirect } from './redirects';
import { BUILDER_CHATS } from './BuilderChats';
import { SessionsLayoutRoute } from './SessionsLayoutRoute';

/**
 * With brief template levels on, the builder has a read-only mode for templates at a level the
 * caller cannot manage, so viewing is enough to open it. Off, it is the editor it always was.
 */
function BriefTemplateBuilderRoute({ children }: { children: ReactNode }) {
  const levelsEnabled = useBooleanFlagValue('brief_template_levels', false);
  return (
    <ProtectedRoute
      canAccess={(p) => (levelsEnabled ? p.canViewBriefTemplates : p.canManageBriefTemplates)}
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * Retired approval chain URLs land on the project. Absolute rather than a
 * relative `..`: from an index or splat route that resolves back to the segment
 * it sits on, which is a redirect to itself.
 */
function RetiredApprovalChainsRedirect() {
  const { platformProjectId } = useParams<{ platformProjectId: string }>();
  return <Navigate to={ROUTES.PROJECT_OVERVIEW(platformProjectId ?? '')} replace />;
}

export const router = createBrowserRouter([
  /**
   * Unified dashboard shell — `DashboardLayout` is mounted ONCE here so
   * cross-route navigations (Home → IQ → Projects → KH → Tools …)
   * preserve the layout instance. This is what keeps motion's
   * `LayoutGroup` context alive in `SidebarRail` so the active
   * indicator can slide smoothly between any two dashboard nav items.
   *
   * Routes that need their own visual shell (chat sessions,
   * Admin) live OUTSIDE this group below.
   */
  {
    element: <DashboardLayout />,
    errorElement: <NotFound />,
    // Cold-loading a lazy route during initial hydration renders this instead
    // of a blank page while the chunk resolves. In-app (SPA) navigations use
    // the parent layout's own loading states.
    hydrateFallbackElement: (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    ),
    children: [
      {
        path: '/',
        children: [
          {
            index: true,
            element: <HomePageDashboard />,
          },
          {
            path: ROUTE_PATTERNS.AGENT_DETAIL,
            element: <AgentDetail />,
          },
          {
            path: '*',
            element: <NotFoundRoute />,
          },
        ],
      },
      {
        path: ROUTE_PATTERNS.KNOWLEDGE_HUB,
        element: (
          <ProtectedRoute canAccess={(p) => p.canAccessKnowledgeHub} awaitWorkspacePermissions>
            <PaddedOutlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <KnowledgeHub />,
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_KB,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewAnyKnowledgeBase}>
                <KnowledgeBase />
              </ProtectedRoute>
            ),
          },
          {
            path: `${ROUTE_PATTERNS.KNOWLEDGE_HUB_KB}/${ROUTE_PATTERNS.KNOWLEDGE_HUB_KB_DETAIL}`,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewAnyKnowledgeBase}>
                <KnowledgeBaseDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_AUDIENCES,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewAudiences}>
                <Audiences />
              </ProtectedRoute>
            ),
          },
          {
            path: `${ROUTE_PATTERNS.KNOWLEDGE_HUB_AUDIENCES}/${ROUTE_PATTERNS.KNOWLEDGE_HUB_AUDIENCE_DETAIL}`,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewAudiences}>
                <AudienceDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_TEMPLATES,
            element: (
              <ProtectedRoute canAccess={(p) => p.canManageEmailTemplates}>
                <EmailTemplatesList />
              </ProtectedRoute>
            ),
          },
          {
            path: `${ROUTE_PATTERNS.KNOWLEDGE_HUB_TEMPLATES}/${ROUTE_PATTERNS.KNOWLEDGE_HUB_TEMPLATE_DETAIL}`,
            element: (
              <ProtectedRoute canAccess={(p) => p.canManageEmailTemplates}>
                <EmailTemplateEditor />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_VISUAL_GUIDELINES,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewBrandVisualGuidelines}>
                <VisualGuidelines />
              </ProtectedRoute>
            ),
          },
          {
            path: `${ROUTE_PATTERNS.KNOWLEDGE_HUB_VISUAL_GUIDELINES}/${ROUTE_PATTERNS.KNOWLEDGE_HUB_VISUAL_GUIDELINES_FOLDER}`,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewBrandVisualGuidelines}>
                <FolderGuidelines />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_BRIEF_TEMPLATES,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewBriefTemplates}>
                <FeatureFlagGuard flag="projects_module">
                  <BriefTemplatesList />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_BRIEF_TEMPLATE_NEW,
            element: (
              <ProtectedRoute canAccess={(p) => p.canManageBriefTemplates}>
                <FeatureFlagGuard flag="projects_module">
                  <BriefTemplateComingSoon mode="new" />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_BRIEF_TEMPLATE_DETAIL,
            element: (
              <BriefTemplateBuilderRoute>
                <FeatureFlagGuard flag="projects_module">
                  <BriefTemplateBuilder />
                </FeatureFlagGuard>
              </BriefTemplateBuilderRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_BRAND_MEDIA,
            element: (
              <ProtectedRoute canAccess={(p) => p.canOpenBrandAssets}>
                <FeatureFlagGuard flag="content_studio_mvp">
                  <BrandMediaLibraryPage />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            // The four taxonomy tabs share ONE layout route: the header, tab
            // bar and content frame mount once and each tab renders into the
            // outlet, so switching tabs re-renders only the frame below rather
            // than remounting (and re-animating) the whole page.
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_TAXONOMY,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewTaxonomy}>
                <FeatureFlagGuard flag="knowledge_hub_taxonomy">
                  <TaxonomyLayout />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
            children: [
              { index: true, element: <TaxonomyList /> },
              { path: 'categories', element: <TaxonomyCategories /> },
              { path: 'fields', element: <TaxonomyFields /> },
              // One route for the index and an open list, so the list sheet stays mounted when
              // Create turns into editing.
              { path: 'lists/:listId?', element: <TaxonomyLists /> },
            ],
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_TAXONOMY_DETAIL,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewTaxonomy}>
                <FeatureFlagGuard flag="knowledge_hub_taxonomy">
                  <TaxonomyDetail />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_MEDIA,
            children: [
              {
                index: true,
                element: (
                  <FeatureFlagGuard flag="knowledge_media">
                    <MediaConnectors />
                  </FeatureFlagGuard>
                ),
              },
              {
                path: ROUTE_PATTERNS.KNOWLEDGE_HUB_MEDIA_DETAIL,
                element: (
                  <FeatureFlagGuard flag="knowledge_media">
                    <MediaConnectors />
                  </FeatureFlagGuard>
                ),
              },
            ],
          },
          {
            path: ROUTE_PATTERNS.KNOWLEDGE_HUB_MEDIA_GUIDELINES,
            element: (
              <FeatureFlagGuard flag="media_guidelines">
                <MediaGuidelines />
              </FeatureFlagGuard>
            ),
          },
        ],
      },
      {
        path: ROUTE_PATTERNS.KNOWLEDGE_BASES,
        element: <Navigate to={ROUTES.KNOWLEDGE_HUB_KB} replace />,
      },
      {
        path: `${ROUTE_PATTERNS.KNOWLEDGE_BASES}/:knowledgeBaseId`,
        element: <LegacyKBRedirect />,
      },
      {
        path: ROUTE_PATTERNS.IQ,
        element: (
          <ProtectedRoute canAccess={(permissions) => permissions.canAccessIq}>
            <ManagedToolGuard toolId="iq">
              <Outlet />
            </ManagedToolGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <AnalysisHome />,
          },
          {
            path: ROUTE_PATTERNS.IQ_NEW_ANALYSIS,
            element: <NewAnalysis />,
          },
          {
            path: ROUTE_PATTERNS.IQ_ANALYSIS,
            element: <AnalysisResult />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTE_PATTERNS.IQ_SECTION_EXECUTIVE_SNAPSHOT} replace />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_EXECUTIVE_SNAPSHOT,
                element: <ExecutiveSnapshot />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_BUSINESS_CHALLENGE,
                element: <BusinessChallenge />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_CULTURE_CONTEXT,
                element: <CultureContext />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_BRAND_XRAY,
                element: <BrandXray />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_CATEGORY_COMPETITION,
                element: <CategoryCompetition />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_BUSINESS_OPPORTUNITIES,
                element: <BusinessOpportunities />,
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_AUDIENCE_PROFILES,
                element: <AudienceProfiles />,
                children: [
                  {
                    path: ROUTE_PATTERNS.IQ_AUDIENCE_PERSONA_DETAIL,
                    element: <IQPersonaDetail />,
                  },
                ],
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_MARKETING_PERFORMANCE,
                element: (
                  <ProtectedRoute canAccess={(p) => p.canAccessYougov}>
                    <FeatureFlagGuard flag="iq_yougov">
                      <MarketingPerformance />
                    </FeatureFlagGuard>
                  </ProtectedRoute>
                ),
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_MEDIA_LANDSCAPE,
                element: (
                  <ProtectedRoute canAccess={(p) => p.canAccessPathmatics}>
                    <FeatureFlagGuard flag="iq_pathmatics">
                      <MediaLandscape />
                    </FeatureFlagGuard>
                  </ProtectedRoute>
                ),
              },
              {
                path: ROUTE_PATTERNS.IQ_SECTION_CREATIVE_LANDSCAPE,
                element: (
                  <ProtectedRoute canAccess={(p) => p.canAccessPathmatics}>
                    <FeatureFlagGuard flag="iq_pathmatics">
                      <CreativeLandscape />
                    </FeatureFlagGuard>
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: ROUTE_PATTERNS.PROJECTS,
        element: (
          <FeatureFlagGuard flag="projects_module">
            <Outlet />
          </FeatureFlagGuard>
        ),
        children: [
          // Projects index owns its own padded wrapper at the page root
          // (see Projects.tsx). NewProject and ProjectDetail are bleed
          // pages — they manage their own scroll/padding internally.
          { index: true, element: <Projects /> },
          {
            path: ROUTE_PATTERNS.NEW_PROJECT,
            element: <NewProject />,
          },
          {
            path: ROUTE_PATTERNS.PROJECT_DETAIL,
            element: <ProjectDetail />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTE_PATTERNS.PROJECT_SECTION_OVERVIEW} replace />,
              },
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_OVERVIEW,
                element: <Overview />,
              },
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_BRIEFING,
                handle: { section: ROUTE_PATTERNS.PROJECT_SECTION_BRIEFING },
                children: [
                  { index: true, element: <Briefing /> },
                  {
                    path: ROUTE_PATTERNS.PROJECT_BRIEF_DETAIL,
                    handle: { hidesProjectChrome: true },
                    element: <ProjectBriefDetail />,
                  },
                ],
              },
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_LIBRARY,
                element: <Library />,
              },
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_DELIVERY,
                handle: { section: ROUTE_PATTERNS.PROJECT_SECTION_DELIVERY },
                // The guard goes on each child rather than on a wrapper route. A wrapper
                // would have to render its own <Outlet />, and that outlet publishes no
                // context, so the project this section is scoped to would not reach
                // Delivery or JobReview.
                children: [
                  {
                    index: true,
                    element: (
                      <FeatureFlagGuard flag="media_delivery">
                        <Delivery />
                      </FeatureFlagGuard>
                    ),
                  },
                  {
                    path: ROUTE_PATTERNS.PROJECT_DELIVERY_JOB,
                    element: (
                      <FeatureFlagGuard flag="media_delivery">
                        <JobReview />
                      </FeatureFlagGuard>
                    ),
                  },
                ],
              },
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_PERFORMANCE,
                handle: { section: ROUTE_PATTERNS.PROJECT_SECTION_PERFORMANCE },
                // The guard goes on each child rather than on a wrapper route, for the
                // same reason Delivery does it: a wrapper would have to render its own
                // <Outlet />, and that outlet publishes no context, so the project this
                // section is scoped to would not reach Performance or its tabs.
                children: [
                  {
                    element: (
                      <FeatureFlagGuard flag="media_performance">
                        <Performance />
                      </FeatureFlagGuard>
                    ),
                    children: [
                      { index: true, element: <PerformanceOverview /> },
                      {
                        path: ROUTE_PATTERNS.PROJECT_PERFORMANCE_PACING,
                        element: <Pacing />,
                      },
                      {
                        path: ROUTE_PATTERNS.PROJECT_PERFORMANCE_CREATIVE,
                        element: <Creative />,
                      },
                      {
                        path: ROUTE_PATTERNS.PROJECT_PERFORMANCE_AUDIENCE,
                        element: <Audience />,
                      },
                      {
                        path: ROUTE_PATTERNS.PROJECT_PERFORMANCE_CAMPAIGNS,
                        element: <ManageCampaigns />,
                      },
                    ],
                  },
                ],
              },
              // Approval chains are a drawer on the project now, opened from the
              // project actions menu. Bookmarks and open tabs on the old
              // full-page URLs land on the project rather than a 404.
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_APPROVAL_CHAINS,
                children: [
                  { index: true, element: <RetiredApprovalChainsRedirect /> },
                  { path: '*', element: <RetiredApprovalChainsRedirect /> },
                ],
              },
              {
                path: ROUTE_PATTERNS.PROJECT_SECTION_FOLDERS,
                // eslint-disable-next-line i18next/no-literal-string -- route handle discriminator the layout matches to mark the active section
                handle: { section: 'folders' },
                element: <ApprovalsGuardedOutlet />,
                children: [
                  {
                    index: true,
                    element: <FoldersListPage />,
                  },
                  {
                    path: ROUTE_PATTERNS.PROJECT_FOLDER_DETAIL,
                    children: [
                      {
                        index: true,
                        element: <FolderDetailPage />,
                      },
                      {
                        path: ROUTE_PATTERNS.PROJECT_FILE_DETAIL,
                        handle: { hidesProjectChrome: true },
                        element: <AssetDetail />,
                      },
                      {
                        path: ROUTE_PATTERNS.PROJECT_FILE_COMPARE,
                        element: <AssetCompareView />,
                      },
                    ],
                  },
                ],
              },

              // Redirects from removed section paths
              {
                path: 'approvals',
                element: <Navigate to={`../${ROUTE_PATTERNS.PROJECT_SECTION_LIBRARY}`} replace />,
              },
              {
                path: 'artifacts',
                element: <Navigate to={`../${ROUTE_PATTERNS.PROJECT_SECTION_FOLDERS}`} replace />,
              },
              {
                path: 'performance',
                element: <Navigate to={`../${ROUTE_PATTERNS.PROJECT_SECTION_OVERVIEW}`} replace />,
              },
              {
                path: 'details',
                element: <Navigate to={`../${ROUTE_PATTERNS.PROJECT_SECTION_OVERVIEW}`} replace />,
              },
            ],
          },
        ],
      },
      {
        // Usage analytics is an admin surface: the numbers cover the whole
        // workspace, not the signed-in user's own activity.
        path: ROUTE_PATTERNS.ANALYTICS,
        element: (
          <ProtectedRoute canAccess={(p) => p.isWorkspaceAdmin}>
            <FeatureFlagGuard flag="usage_analytics">
              <Analytics />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
      },
      {
        // A single entry point: the list at the index route, the open creative system as its
        // one child route. One path per board - which tab of it you are on, and which view the
        // Output tab is drawn in, ride in the query string rather than splitting the route.
        path: ROUTE_PATTERNS.CONTENT_STUDIO,
        element: (
          <ContentStudioRouteGuard>
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </ContentStudioRouteGuard>
        ),
        children: [
          {
            index: true,
            element: <ContentBoardsPage />,
          },
          {
            path: ROUTE_PATTERNS.CONTENT_STUDIO_SYSTEM,
            element: <ContentStudioPage />,
          },
        ],
      },
      {
        path: ROUTE_PATTERNS.TRACKERS,
        element: (
          <FeatureFlagGuard flag="trackers_module">
            <PaddedOutlet />
          </FeatureFlagGuard>
        ),
        children: [
          { index: true, element: <Trackers /> },
          { path: ROUTE_PATTERNS.TRACKER_DETAIL, element: <TrackerBoard /> },
        ],
      },
      {
        path: ROUTE_PATTERNS.TOOLS,
        element: <Outlet />,
        children: [
          {
            path: ROUTE_PATTERNS.TOOLS_FOCUS_GROUPS,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessFocusGroups}>
                <ManagedToolGuard toolId="focus-groups">
                  <FocusGroups />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
          {
            index: true,
            element: <ToolsHub />,
          },
          {
            path: ROUTE_PATTERNS.TOOLS_FOCUS_GROUPS_NEW,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessFocusGroups}>
                <ManagedToolGuard toolId="focus-groups">
                  <NewFocusGroup />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_FOCUS_GROUP_DETAIL,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessFocusGroups}>
                <ManagedToolGuard toolId="focus-groups">
                  <FocusGroupDetail />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_OPPORTUNITIES,
            element: (
              <ManagedToolGuard toolId={SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER}>
                <OpportunitiesRoom />
              </ManagedToolGuard>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_NACB_INTAKE,
            element: (
              <ProtectedRoute canAccess={(p) => p.canOpenTool('nacb-intake')}>
                <FeatureFlagGuard flag="nacb_trigger_intake">
                  <ManagedToolGuard toolId="nacb-intake">
                    <NACBIntakeFormPage />
                  </ManagedToolGuard>
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            // These four static category routes shadow the dynamic
            // `:category` route below — they match first, so PlanPage/Create/
            // Scale/Deliver always win for their category and ToolsCategoryPage
            // (the generic fallback) never renders for them. See the note on
            // ToolsCategoryPage before assuming it is reachable.
            path: ROUTE_PATTERNS.TOOLS_PLAN,
            element: <PlanPage />,
          },
          {
            path: ROUTE_PATTERNS.TOOLS_CREATE,
            element: <CreatePage />,
          },
          {
            path: ROUTE_PATTERNS.TOOLS_DELIVER,
            element: <DeliverPage />,
          },
          {
            path: ROUTE_PATTERNS.TOOLS_SCALE,
            element: <ScalePage />,
          },
          {
            // Dynamic fallback for any category WITHOUT a dedicated static
            // route above. Dormant today because plan/create/scale/deliver all
            // have static routes; only fires for a new TOOL_CATEGORIES entry
            // that lacks one. See the note on ToolsCategoryPage.
            path: ROUTE_PATTERNS.TOOLS_HUB_CATEGORY,
            element: <ToolsCategoryPage />,
          },
        ],
      },
      {
        path: ROUTE_PATTERNS.TOOLS,
        children: [
          {
            // Declared before the detail route so the static segment is matched
            // as the index rather than as a `:datasetId`.
            path: ROUTE_PATTERNS.TOOLS_TAXONOMY_GENERATION,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewTaxonomy}>
                <FeatureFlagGuard flag="knowledge_hub_taxonomy">
                  <TaxonomyGenerationList />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_TAXONOMY_GENERATION_DETAIL,
            element: (
              <ProtectedRoute canAccess={(p) => p.canViewTaxonomy}>
                <FeatureFlagGuard flag="knowledge_hub_taxonomy">
                  <TaxonomyGenerationDetail />
                </FeatureFlagGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_CONTENT_TEST,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessTestContent}>
                <ManagedToolGuard toolId="test-content">
                  <ContentTestOverview />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_CONTENT_TEST_NEW,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessTestContent}>
                <ManagedToolGuard toolId="test-content">
                  <ContentTestSetup />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_CONTENT_TEST_DETAIL,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessTestContent}>
                <ManagedToolGuard toolId="test-content">
                  <ContentTestDetail />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATTERNS.TOOLS_FOCUS_GROUP_DETAIL,
            element: (
              <ProtectedRoute canAccess={(p) => p.canAccessFocusGroups}>
                <ManagedToolGuard toolId="focus-groups">
                  <FocusGroupDetail />
                </ManagedToolGuard>
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTE_PATTERNS.SESSIONS,
    element: <DashboardLayout />,
    children: [
      {
        index: true,
        Component: Sessions,
      },
    ],
  },
  {
    path: ROUTE_PATTERNS.MY_MCPS,
    element: <DashboardLayout />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute canAccess={() => true}>
            <FeatureFlagGuard flag="user_managed_mcp_servers">
              <MyMcpsPage />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Standalone, chrome-less download route: renders a single document (no app
  // shell) so it can be printed to PDF (browser Cmd+P, or a headless browser via
  // page.goto). Unknown document types 404 inside <Download/> via the
  // downloadables registry.
  {
    path: ROUTE_PATTERNS.DOWNLOAD_DOCUMENT,
    element: <Download />,
    errorElement: <NotFound />,
  },
  // Routes below have their own visual shell and intentionally do NOT
  // share the unified DashboardLayout mount (different chrome/sidebar):
  //   - Chat sessions render inside SessionsLayoutRoute (SessionContent shell).
  //   - Admin renders inside OrganizationSettingsLayout.
  // Migrating those into the unified shell is a future ticket.
  {
    path: ROUTE_PATTERNS.NEW_CHAT_SESSION,
    element: <SessionsLayoutRoute />,
    children: [
      {
        index: true,
        element: <Root />,
      },
    ],
  },
  {
    path: ROUTE_PATTERNS.CHAT_SESSION,
    element: <SessionsLayoutRoute />,
    children: [
      {
        index: true,
        element: <StreamChatSession />,
      },
    ],
  },
  {
    path: ROUTE_PATTERNS.ADMIN,
    element: <OrganizationSettingsLayout />,
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <AdminIndexRedirect />,
      },
      {
        path: ROUTE_PATTERNS.ADMIN_MODELS,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageModelsWhitelisting}>
            <ModelProvidersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_ETL_CONFIG,
        element: (
          <ProtectedRoute canAccess={(p) => p.isInstanceAdmin}>
            <EtlConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_AD_FORMATS,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageAdFormats}>
            <FeatureFlagGuard flag="content_studio_mvp">
              <AdFormatsPage />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_TOOLS,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageTools}>
            <ToolsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_CUSTOMIZATION,
        element: (
          <ProtectedRoute canAccess={(p) => p.isWorkspaceAdmin}>
            <CustomizationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_MCP_CATALOG,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageTools}>
            <FeatureFlagGuard flag="user_managed_mcp_servers">
              <McpCatalogPage />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_CONNECTED_APPS,
        element: (
          // Lists the signed-in user's own grants, so it needs no admin permission.
          <ProtectedRoute canAccess={() => true}>
            <FeatureFlagGuard flag="connected_apps">
              <ConnectedAppsPage />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_EXTERNAL_TOOLS_AGENT_CONFIG,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageTools}>
            <ExternalToolsAgentConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_AUDIENCE_AGENT_CONFIG,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageTools}>
            <AudienceAgentConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_CREATE_AGENT,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageUserAgents}>
            <AgentBuilderPage chats={BUILDER_CHATS} />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_WIDGET,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageUserAgents}>
            <RequireWidgets>
              <WidgetRedirect />
            </RequireWidgets>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_EDIT_AGENT,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageUserAgents}>
            <AgentBuilderPage chats={BUILDER_CHATS} />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_AGENT_CONFIG,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageTools}>
            <AgentConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATTERNS.ADMIN_TOOL_CONFIG,
        element: (
          <ProtectedRoute canAccess={(p) => p.canManageTools}>
            <ToolConfigPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);
